import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { useLanguage } from '../../context/LanguageContext';
import { renderLevelText } from '../../utils/learningText';
import { BORDER, INK, MUTED, SURFACE } from '../../pages/Practice/map/mapTokens';
import { ANCHOR_SCROLL_MARGIN, CARD_CAP, CONTENT_MAX_WIDTH, SOFT_BLUE, ctaGradientSx } from '../../styles/tokens';
import { PRACTICE_FLOW, SECTION_IDS } from './homeContent';
import { HomeIcon, SectionHeading, bandSx } from './homeUi';

/**
 * The two practice routes as two self-contained cards.
 *
 * Each card holds its own content, note and single action, so a reader never has
 * to work out which sample or which button belongs to which route.
 *
 * Visually these carry more weight than the rest of the page on purpose. Every
 * other section is a white card on whitesmoke with a hairline border, which is
 * calm but gives the eye nothing to catch on — so this one gets a filled header
 * band, a real level track and solid buttons in the route's own colour.
 */

/**
 * The oral exam's actual shape: one entry point at A2, then a fork.
 *
 * Everyone sits the same opening; the examiner decides part-way through whether
 * you continue on the A1–A2 or the A2–B1 track. That branch is the single most
 * confusing thing about the format, so it is drawn rather than described.
 */
const LevelPath = ({ flow }) => {
  const { start, branches } = flow.levelPath;
  const line = { position: 'absolute', bgcolor: '#C9D2E0', borderRadius: 99 };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }}>
        <Box
          sx={{
            width: 62,
            height: 62,
            borderRadius: '50%',
            // A ring rather than a solid fill. The node still reads as the start
            // of the path, but a 62px disc of navy was the heaviest block of
            // colour in the section and it was only labelling a level.
            bgcolor: SURFACE,
            border: `2.5px solid ${flow.accent}`,
            color: flow.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem',
            fontWeight: 900,
            boxShadow: `0 6px 16px -10px ${flow.accent}`,
          }}
        >
          {renderLevelText(start.title)}
        </Box>
        <Typography sx={{ mt: 1, color: MUTED, fontSize: '0.75rem', fontWeight: 700, textAlign: 'center' }}>
          {start.sub}
        </Typography>
      </Box>

      {/* Fork: a stub out of A2, a vertical spine, then a stub into each track.
          The 25%/75% anchors are the vertical centres of the two branch pills. */}
      <Box aria-hidden sx={{ position: 'relative', alignSelf: 'stretch', width: 46, flex: '0 0 auto', mb: 3 }}>
        <Box sx={{ ...line, left: 0, top: 'calc(50% - 1.5px)', width: 20, height: 3 }} />
        <Box sx={{ ...line, left: 20, top: '25%', bottom: '25%', width: 3 }} />
        <Box sx={{ ...line, left: 20, top: 'calc(25% - 1.5px)', width: 26, height: 3 }} />
        <Box sx={{ ...line, left: 20, bottom: 'calc(25% - 1.5px)', width: 26, height: 3 }} />
      </Box>

      <Box sx={{ display: 'grid', gap: 1.25, flexGrow: 1, minWidth: 0, mb: 3 }}>
        {branches.map((branch) => (
          <Box
            key={branch.title}
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 1,
              px: 1.75,
              py: 1.25,
              borderRadius: '12px',
              border: `1px solid ${BORDER}`,
              bgcolor: SOFT_BLUE,
            }}
          >
            <Typography sx={{ color: flow.accent, fontWeight: 900, fontSize: '0.9375rem', whiteSpace: 'nowrap' }}>
              {renderLevelText(branch.title)}
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: '0.8125rem' }}>{branch.sub}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/** A real topic, with the words a visitor would actually find inside it. */
const TopicSample = ({ flow, language }) => {
  const { sample } = flow;

  return (
    <Box>
      <Box
        sx={{
          borderRadius: '14px',
          border: `1px solid ${BORDER}`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ bgcolor: SOFT_BLUE, px: 1.75, py: 1.25, borderBottom: `1px solid ${BORDER}` }}>
          <Typography
            translate="no"
            className="notranslate"
            lang={language === 'de' ? 'de' : 'fr'}
            sx={{ color: flow.accent, fontWeight: 900, fontSize: '1.0625rem' }}
          >
            {language === 'de' ? sample.topic.de : sample.topic.fr}
          </Typography>
        </Box>
        {sample.words.map((word, index) => (
          <Box
            key={word.en}
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 1.5,
              px: 1.75,
              py: 1,
              borderTop: index === 0 ? 'none' : `1px solid ${BORDER}`,
            }}
          >
            <Typography
              translate="no"
              className="notranslate"
              lang={language === 'de' ? 'de' : 'fr'}
              sx={{ color: INK, fontWeight: 700, fontSize: '0.9375rem' }}
            >
              {language === 'de' ? word.de : word.fr}
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>{word.en}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
        {sample.views.map((view) => (
          <Typography
            key={view}
            component="span"
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: '999px',
              border: `1px solid ${BORDER}`,
              color: MUTED,
              fontSize: '0.75rem',
              fontWeight: 700,
            }}
          >
            {view}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

const PracticeFlow = ({ tone }) => {
  const { language } = useLanguage();

  return (
    <Box
      component="section"
      id={SECTION_IDS.practise}
      sx={{ ...bandSx(tone), scrollMarginTop: ANCHOR_SCROLL_MARGIN, '&:focus': { outline: 'none' } }}
    >
      <SectionHeading title={PRACTICE_FLOW.title} subtitle={PRACTICE_FLOW.subtitle} chapter="practise" />

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 2.5, md: 3 },
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          alignItems: 'stretch',
          maxWidth: CONTENT_MAX_WIDTH,
          mx: 'auto',
        }}
      >
        {PRACTICE_FLOW.flows.map((flow) => (
          <Box
            key={flow.key}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              bgcolor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderTop: `4px solid ${CARD_CAP}`,
              borderRadius: '18px',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(16,24,40,.04), 0 22px 44px -24px rgba(29,45,74,.45)',
              transition: 'transform .22s cubic-bezier(.2,.8,.2,1), box-shadow .22s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 2px 4px rgba(16,24,40,.05), 0 30px 52px -24px rgba(29,45,74,.55)',
              },
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '&:hover': { transform: 'none' },
              },
            }}
          >
            {/* A filled header band gives each card a block of its own colour —
                the thin top cap it replaced was too quiet to tell them apart. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: { xs: 2.5, md: 3 },
                py: 1.75,
                borderBottom: `1px solid ${BORDER}`,
              }}
            >
              <HomeIcon name={flow.icon} aria-hidden sx={{ fontSize: 20, color: flow.accent }} />
              <Typography
                component="h3"
                sx={{ color: flow.accent, fontWeight: 900, fontSize: '0.9375rem', letterSpacing: '.02em' }}
              >
                {flow.label}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, px: { xs: 2.5, md: 3 }, py: 2.5 }}>
              <Typography sx={{ color: INK, fontSize: '0.9375rem', lineHeight: 1.6, mb: 2.5 }}>
                {renderLevelText(flow.text)}
              </Typography>

              <Box sx={{ flexGrow: 1, mb: 2.5 }}>
                {flow.levelPath ? <LevelPath flow={flow} /> : <TopicSample flow={flow} language={language} />}
              </Box>

              <Typography sx={{ color: MUTED, fontSize: '0.8125rem', mb: 1.75 }}>{flow.note}</Typography>

              <Button
                component={RouterLink}
                to={flow.to(language)}
                endIcon={<ArrowForward />}
                sx={{ ...ctaGradientSx, alignSelf: 'flex-start' }}
              >
                {flow.cta}
              </Button>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PracticeFlow;
